import React, {useContext, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import styled from 'styled-components';
import GetDirectionBtn from '../../UI/GetDirectionBtn';
import InfoText from '../../UI/InfoText';
import Username from '../../UI/Username';
import Address from '../../UI/Address';
import {useDispatch, useSelector} from 'react-redux';
import {selectActiveUser, selectBrowserCoords, updateBELocationsOnly} from './dashboardSlice';
import {currentUserLocation, locationByID, ORMLocations, usersByID} from '../../app/models/ormSelectors';
import {calculateDistance, convertTime, convertTimeFromNow, usePrevious} from '../../common/helper';
import {BatteryLevel, RefreshSmall} from '../../UI/Icons';
import {fontSize, greenTextColor, spinAnimation, warningColor} from '../../mixins';
import {getReversedGeoCode} from '../../common/api';
import {MapContext} from '../map/MapContext';
import {selectUserInfo} from '../login/loginSlice';
import {selectToken} from '../auth/authSlice';

const StyledTopBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledBattery = styled.div`
  width: fit-content;
  display: flex;  
  justify-content: space-between;
  margin-left: auto;
  
  span {
      display: block;
      ${fontSize(12)};
      line-height: 16px;
      letter-spacing: -0.217059px;
      color: #6E6E6E;
      margin-right: 4px;
}
`

const StyledPermissionError = styled.span`
  color: ${warningColor};
  ${fontSize(12)};
  line-height: 16px;
`

const StyledRefresh = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  margin-left: 8px;
  
  svg {
    ${props => props.animate && spinAnimation};
  }
`


const InfoPopup = ({isTrackimo}) => {
    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const ormLocations = useSelector(ORMLocations);
    const activeUser = useSelector(selectActiveUser);
    const geoByID = useSelector(locationByID);
    const userByID = useSelector(usersByID);
    const initialCoords = {lat: useContext(MapContext).lat, lng: useContext(MapContext).lng};
    const currentUserPosition = useSelector(currentUserLocation)[0] || initialCoords;
    const currentUserCoords = useSelector(currentUserLocation)[0];
    const browserLat = useSelector(selectBrowserCoords).browserLat;
    const browserLng = useSelector(selectBrowserCoords).browserLng;
    const [formattedAddress, setFormattedAddress] = useState(null);
    const [permissionError, setPermissionError] = useState('');
    const [locationCreation, setLocationCreation] = useState(null);
    const textColor = locationCreation === 'Now' ? greenTextColor : null;
    const prevCurrentLat = usePrevious(currentUserPosition.lat);
    const prevCurrentLng = usePrevious(currentUserPosition.lng);
    const batteryLevel = userByID(activeUser).battery_level;
    const [since, setSince] = useState('');
    const [refresh, setRefresh] = useState(false);
    const {locationStatus} = geoByID(activeUser);
    const shouldHideAddress = locationStatus === 'OLDER_THAN_FIVE_MINS' || locationStatus === 'OLDER_THAN_24H';

    const onRefreshClick = () => {
        setRefresh(true);
        dispatch(updateBELocationsOnly(token, setRefresh));
    };

    const getAddress = () => {
        if (!prevCurrentLat && !prevCurrentLng) {
            getReversedGeoCode(currentUserPosition.lat, currentUserPosition.lng).then(res => setFormattedAddress(res));
            return;
        }

        const distance = calculateDistance(currentUserPosition.lat, currentUserPosition.lng, prevCurrentLat, prevCurrentLng);

        if (distance >= 200) {
            getReversedGeoCode(currentUserPosition.lat, currentUserPosition.lng).then(res => setFormattedAddress(res));
            return;
        }

        return null;
    }

    const setPermissionStatus = () => {
        const userData = userByID(activeUser);
        const locationData = geoByID(activeUser);

        if (!locationData.location) {
            return 'No location to show';
        }

        if (userData.push_service_disabled) {
            return 'Push notifications disabled';
        }

        if (userData.sign_out) {
            return 'User has signed out';
        }

        if (locationData.background_refresh_disabled) {
            return 'Background refresh disabled';
        }

        if (userData.geo_services_disabled) {
            return 'Location services should be set to “Always” if you want Geozilla to work correctly.'
        }

        if (userData.battery_level <= 5 && userData.battery_level > 0) {
            return `${userData.name}’s battery is low`
        }

        if (userData.incognito_mode) {
            return 'User is in Incognito Mode'
        }

        return '';
    };

    useEffect(() => {
        const userSince = geoByID(activeUser).location?.there_since;

        userSince
            ? setSince(convertTime(moment, userSince))
            : setSince('');

    }, [activeUser]);

    useEffect(() => {
        if (activeUser && ormLocations) {
            if (geoByID(activeUser).location) {
                if (geoByID(activeUser).locationStatus === 'ONLINE') {
                    return setLocationCreation('Now')
                }

                const formattedTime = convertTimeFromNow(moment, geoByID(activeUser).location?.creation_time);

                return setLocationCreation(formattedTime);
            } else {
                setLocationCreation(null);
            }
        }

    }, [activeUser, ormLocations]);

    useEffect(() => {
        if (activeUser) {
            if (!currentUserCoords) {
                return setFormattedAddress(null)
            }

            getAddress();
        }
    }, [activeUser, currentUserCoords]);

    useEffect(() => {
        const errorTxt = setPermissionStatus();
        setPermissionError(errorTxt);
    }, [activeUser, userByID(activeUser)]);

    return (
        <>
            <StyledTopBlock>
                <InfoText color={textColor}>{locationCreation}</InfoText>

                {batteryLevel > 0 && (
                    <StyledBattery>
                        <span>{batteryLevel}%</span>
                        <BatteryLevel level={userByID(activeUser).battery_level}/>
                    </StyledBattery>
                )}

                {isTrackimo && <StyledRefresh onClick={onRefreshClick} animate={refresh} id='continue-button'>{RefreshSmall}</StyledRefresh>}
            </StyledTopBlock>

            <Username>{userByID(activeUser).name}</Username>
            <Address isDisabled={locationCreation !== 'Now'}>
                {formattedAddress}
            </Address>
            {(isTrackimo && shouldHideAddress) && <Address isDisabled={true}>Fetching location...</Address>}
            {since && <InfoText>{`Since ${since}`}</InfoText>}
            <StyledPermissionError>
                {permissionError}
            </StyledPermissionError>
            {geoByID(activeUser).location && <GetDirectionBtn
                browserLat={browserLat}
                browserLng={browserLng}
                lat={currentUserCoords.lat}
                lng={currentUserCoords.lng}
            >Get Directions</GetDirectionBtn>}
        </>
    )
};


export default InfoPopup;

